import React, { useContext, useEffect } from 'react';
import Input from '@ferris/react/components/forms/Input';
import { connect } from 'react-redux';
import { hot } from 'react-hot-loader';
import PropTypes from 'prop-types';
import { TrackingContext } from '@vrbo/react-event-tracking';
import PhoneInputWrapper from '../../Input/PhoneInputWrapper';
import withPageFlow from '../../PageFlow/PageFlow';
import TranslationUtil from '../../../utils/TranslationUtil';
import LeadActions from '../../../actions/lead';
import MissingDetailsSubmit from './MissingDetailsSubmit';
import { createOutcomeEvent, createPageViewEvent } from '../../../utils/EventProviders/EgClickstreamUtil';
import ExperimentUtil from '../../../utils/ExperimentUtil';
import { getCookie } from '../../../utils/CookieUtil';
import { isPhoneSecureListFTEnabled } from '../../../utils/ExperimentsUtil';

export const MissingDetails = (props) => {
    const {
        setNewSupplier,
        setPassword,
        referrer,
        setPublicUuid,
        setAutoAuthUrl,
        pageFlow,
        title,
        passwordLabel,
        phoneValidation,
        ctaLabel,
        hasPhoneNumber,
        hasPasswordSet,
        password
    } = props;
    const context = useContext(TrackingContext);
    const { trigger } = context;
    useEffect(() => {
        window.scrollTo(0, 0);
        trigger('pageLoad', { pageName: 'partner-qualifier: MissingDetails', referrer });
        trigger('pageview', createPageViewEvent('missing_details'));
        return () => {
            trigger('pageUnload', { pageName: 'partner-qualifier: MissingDetails', referrer });
        };
    });

    const onCompleted = (data) => {
        const { type: contactType, vrboContact } = data.updateContact;
        switch (contactType) {
        case 'FRBO':
            // May need to update the below structure once the gql changes are merged
            setPublicUuid(vrboContact.userPublicUuid);
            setNewSupplier(vrboContact.newSupplier);
            setAutoAuthUrl(vrboContact.autoAuthUrl);
            if (vrboContact.newSupplier) {
                trigger('userAccountCreated', { publicuuid: vrboContact.userPublicUuid });
                trigger('outcome', createOutcomeEvent('user_account_creation.succeeded', 'MissingDetails', 'UpgradeAccount', vrboContact.userPublicUuid));
            } else {
                trigger('outcome', createOutcomeEvent('user_account_upgraded.succeeded', 'MissingDetails', 'UpgradeAccount', vrboContact.userPublicUuid));
            }
            pageFlow.next();
            break;

        default:
            throw new Error(`Unsupported contactType '${contactType}' returned by service`);
        }
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
    };
    const validPhone = phoneValidation.isValid
    && (phoneValidation.validationService && phoneValidation.validationService.isValid);
    return (
        <div className="ulx-missing-details">
            <h1>{title}</h1>
            <div className="ulx-form__form fs-block">
                {!hasPhoneNumber && <PhoneInputWrapper />}
                {!hasPasswordSet && (
                    <Input
                        id="password-input"
                        classes="password-input"
                        fullWidth
                        label={passwordLabel}
                        type="password"
                        isControlled
                        floatLabel
                        data-wdio="password-input"
                        value={password}
                        onChange={handlePasswordChange}
                        onBlur={handlePasswordChange}
                    />
                )}
                <div className="missing-details-submit">
                    <MissingDetailsSubmit
                        label={ctaLabel}
                        onCompleted={onCompleted}
                        onError={() => {}}
                        {...props}
                        validPhone={validPhone}
                    />
                </div>
            </div>
        </div>
    );
};

MissingDetails.propTypes = {
    title: PropTypes.string.isRequired,
    passwordLabel: PropTypes.string.isRequired,
    ctaLabel: PropTypes.string.isRequired,
    setPassword: PropTypes.func.isRequired,
    password: PropTypes.string.isRequired,
    passwordValidation: PropTypes.shape({
        isValid: PropTypes.bool.isRequired,
        error: PropTypes.string
    }).isRequired,
    hasPhoneNumber: PropTypes.bool.isRequired,
    hasPasswordSet: PropTypes.bool.isRequired,
    referrer: PropTypes.string.isRequired,
    setNewSupplier: PropTypes.func.isRequired,
    setPublicUuid: PropTypes.func.isRequired,
    setAutoAuthUrl: PropTypes.func.isRequired,
    phoneCountryCode: PropTypes.string.isRequired,
    phoneValidation: PropTypes.shape({
        validationService: PropTypes.shape({
            success: PropTypes.bool,
            isValid: PropTypes.bool
        }),
        isValid: PropTypes.bool,
        error: PropTypes.string
    }).isRequired,
    phoneCountryCodeDigits: PropTypes.string.isRequired,
    phoneNumber: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
    displayLocale: PropTypes.string.isRequired,
    pageFlow: PropTypes.shape({
        next: PropTypes.func
    }).isRequired,
    fingerprint: PropTypes.shape({
        orgId: PropTypes.string,
        deviceId: PropTypes.string
    }).isRequired,
    addPhoneToSecureListEnabled: PropTypes.bool.isRequired,
    duaid: PropTypes.string.isRequired
};

/* istanbul ignore next */
const mapStateToProps = (state) => {
    const { app, lead } = state;
    const { referrer, fingerprint } = app;
    const { password, missingInfo, passwordValidation } = lead;
    const {
        phoneValidation, phoneCountryCode, phoneNumber, phoneCountryCodeDigits
    } = lead;
    const { hasPhoneNumber, hasPasswordSet } = missingInfo || {};
    const translations = new TranslationUtil(app.translations, app.fallbackTranslations);
    const experimentUtil = new ExperimentUtil(app.experiments);
    const addPhoneToSecureListEnabled = isPhoneSecureListFTEnabled(experimentUtil);

    const duaid = getCookie('DUAID');
    return ({
        title: translations.getTranslation('lyp.leadForm.title'),
        passwordLabel: translations.getTranslation('lyp.leadForm.password.label'),
        ctaLabel: translations.getTranslation('Vrbo.cta.next'),
        password,
        hasPhoneNumber,
        hasPasswordSet,
        referrer,
        phoneCountryCode,
        phoneValidation,
        phoneCountryCodeDigits,
        phoneNumber,
        locale: app.locale.canonical,
        displayLocale: app.locale.display,
        fingerprint,
        addPhoneToSecureListEnabled,
        duaid,
        passwordValidation
    });
};

/* istanbul ignore next */
const mapDispatchToProps = dispatch => ({
    setPassword: password => dispatch(LeadActions.setPassword(password)),
    setNewSupplier: newSupplier => dispatch(LeadActions.setNewSupplier(newSupplier)),
    setPublicUuid: publicUuid => dispatch(LeadActions.setPublicUuid(publicUuid)),
    setAutoAuthUrl: autoAuthUrl => dispatch(LeadActions.setAutoAuthUrl(autoAuthUrl))
});

const ConnectedMissingDetails = connect(mapStateToProps, mapDispatchToProps)(MissingDetails);
ConnectedMissingDetails.displayName = 'MissingDetails';
export default hot(module)(withPageFlow(ConnectedMissingDetails));
